import { ReactNode } from "react";

import { RightOutlined } from "@ant-design/icons";
import { Dropdown, Menu, MenuProps, Typography } from "antd";
import { cx } from "class-variance-authority";
import { Link } from "react-router-dom";

import {
    ActionDropdownItem,
    ActionDropdownSpec,
    ActionLink,
    ActionSpec,
} from "../../componentTypes/action";
import moreSvg from "../../img/more.svg";
import { Svg } from "../../utils/svgIcon";
import { Tooltip } from "../Tooltip";

import * as theme from "./ActionMenu.css";

type MenuItem = NonNullable<MenuProps["items"]>[number];

// antd types `theme` as "light" | "dark", but the overflow popup's styles key off this custom one
const ACTION_MENU_THEME_PROPS = { theme: theme.ACTION_MENU_THEME } as object;

export interface ActionMenuProps {
    id: string;
    actions: ActionSpec[];
}

export function ActionMenu({ id, actions }: ActionMenuProps) {
    return (
        <Menu
            id={id}
            mode="horizontal"
            className={theme.menu}
            selectable={false}
            triggerSubMenuAction="click"
            items={actions.map(actionItem)}
            overflowedIndicator={<Svg className={theme.dots} src={moreSvg} />}
            {...ACTION_MENU_THEME_PROPS}
        />
    );
}

function actionItem(action: ActionSpec): MenuItem {
    if ("type" in action) {
        return dropdownItem(action);
    }
    const disabled = action.status?.type === "DISABLED";
    const content = (
        <>
            <Svg className={theme.icon} src={action.icon.svg} />
            {action.label}
        </>
    );
    const label =
        action.status?.type === "DISABLED" ? (
            <Tooltip title={action.status.reason}>{content}</Tooltip>
        ) : (
            content
        );
    return {
        key: action.id,
        className: cx(theme.menuItem, disabled && theme.disabledMenuItem),
        onClick: "onClick" in action ? action.onClick : undefined,
        disabled,
        // antd gives links inside disabled menu items pointer-events: none, which would also swallow the tooltip
        label:
            "link" in action && !disabled ? (
                <ActionLinkLabel link={action.link}>{label}</ActionLinkLabel>
            ) : (
                label
            ),
    };
}

interface ActionLinkLabelProps {
    link: ActionLink["link"];
    children: ReactNode;
}

function ActionLinkLabel({ link, children }: ActionLinkLabelProps) {
    return "href" in link ? (
        <a href={link.href}>{children}</a>
    ) : (
        <Link to={link.to}>{children}</Link>
    );
}

function dropdownItem(action: ActionDropdownSpec): MenuItem {
    const disabled = action.status?.type === "DISABLED";
    const divider: MenuItem = {
        type: "divider",
        className: theme.dropdownActionMenuItemDivider,
    };
    const items = action.items.flatMap((item, index) =>
        index === 0 ? [dropdownEntry(item)] : [divider, dropdownEntry(item)]
    );
    return {
        key: action.id,
        className: cx(theme.menuItem, disabled && theme.disabledMenuItem),
        disabled,
        label: (
            <Dropdown
                disabled={disabled}
                menu={{ items, className: theme.dropdownActionMenu }}
                trigger={["click"]}
            >
                <Typography.Link>
                    <Svg className={theme.icon} src={action.icon.svg} />
                    {action.label}
                </Typography.Link>
            </Dropdown>
        ),
    };
}

function dropdownEntry(item: ActionDropdownItem): MenuItem {
    return {
        key: item.key,
        className: theme.dropdownActionMenuItem,
        label: (
            <a href={item.action.link?.href} onClick={item.action.onClick}>
                <div className={theme.dropdownActionMenuItemContent}>
                    <div>
                        <div className={theme.dropdownActionMenuItemTitle}>
                            {item.title}
                        </div>
                        <div
                            className={theme.dropdownActionMenuItemDescription}
                        >
                            {item.description}
                        </div>
                    </div>
                    <RightOutlined />
                </div>
            </a>
        ),
    };
}
